/**
 * Vehicle-shape glyph used in the Garage list, car switcher, and car detail
 * header.
 *
 * Each shape lives as its own `.svg` file in `src/assets/car-icons/`, drawn
 * in a Lucide-style line-icon family (48x24 viewBox, 1.8px stroke,
 * stroke="currentColor", no fill). The files are imported via Vite's SVGR
 * plugin (`?react`), which means anyone can redesign an icon by opening
 * the matching `.svg` file in Figma / Inkscape, exporting, and saving over
 * the existing one — HMR picks it up immediately.
 *
 * Pass `size` for an explicit width/height override; otherwise the SVG
 * fills 1em so it scales with surrounding font-size.
 */

import type { CarIconName } from "../api/types";

import SedanIcon from "../assets/car-icons/sedan.svg?react";
import WagonIcon from "../assets/car-icons/wagon.svg?react";
import CamperIcon from "../assets/car-icons/camper.svg?react";
import PickupIcon from "../assets/car-icons/pickup.svg?react";
import MotorcycleIcon from "../assets/car-icons/motorcycle.svg?react";

interface CarIconProps {
  name: CarIconName;
  size?: number;
  className?: string;
  ariaLabel?: string;
}

const ICONS: Record<CarIconName, typeof SedanIcon> = {
  sedan: SedanIcon,
  wagon: WagonIcon,
  camper: CamperIcon,
  pickup: PickupIcon,
  motorcycle: MotorcycleIcon,
};

export default function CarIcon({
  name,
  size,
  className,
  ariaLabel,
}: CarIconProps) {
  const Svg = ICONS[name];
  const dim = size ? `${size}` : "1em";
  return (
    <Svg
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      width={dim}
      height={dim}
      className={className}
    />
  );
}
